import React, { useState, useRef } from "react";
import {
  ButtonComp,
  CardComp,
  TextInputComp,
  FormikSelectComp,
  FormikDatePicker,
} from "../../../components";
// import AdminLayout from "../../../Layout/AdminLayout";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useGetRolesQuery,
  useGetCountriesQuery,
} from "../../../store/selectableSlice";
import {
  useGetBranchesQuery,
  selectBranchesData,
} from "../../../store/admin/settings/branchSlice";
import { useCreateStaffMutation } from "../../../store/admin/staff";
import { toast } from "react-hot-toast";
import axios from "axios";
import { useSelector } from "react-redux";
import FallbackImage from "../../../components/Common/ImageWrapper";
import { FiUpload, FiX } from "react-icons/fi";
import FormLoadingOverlay from "../../../components/Common/FormLoadingOverlay";
import { useUser } from "../../../hooks";
import {
  isStaffSystemRole,
  staffCanManageRoles,
} from "../../../utils/staffRoleModules";

export const StaffRegistrationHome = () => {
  const [imgFile, setImgFile] = useState();
  const [imgPreview, setImgPreview] = useState(null);
  const [errorImg, setErrorImg] = useState(null);
  const fileInputRef = useRef(null);

  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    dob: "",
    role_id: "",
    branch_id: "",
    address: "",
    country_id: "",
  };

  const [createStaff, { isLoading: isCreating }] = useCreateStaffMutation();

  const updateImgFile = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setImgFile(null);
      setImgPreview(null);
      setErrorImg(null);
      return;
    }
    const size = file?.size / 1024 / 1024;
    const type = file?.type?.split("/")[1];
    const validTypes = ["jpeg", "jpg", "png"];
    if (size > 10 || !validTypes.includes(type)) {
      setErrorImg(
        "Invalid file. Size must be ≤ 10MB and format must be JPG, JPEG, or PNG"
      );
      setImgFile(null);
      setImgPreview(null);
    } else {
      setErrorImg(null);
      setImgFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImgPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImgFile(null);
    setImgPreview(null);
    setErrorImg(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const { data: roles = [] } = useGetRolesQuery();
  const user = useUser();
  const canManageRoles = staffCanManageRoles(user?.role);
  const assignableRoles = (roles || []).filter((role) => {
    if (!role) return false;
    if (role.is_system || role.name === "Super Admin") {
      return isStaffSystemRole(user?.role);
    }
    return true;
  });

  const { isLoading: isLoadingBranches } = useGetBranchesQuery();
  const branches = useSelector(selectBranchesData);

  const { data: countries = [] } = useGetCountriesQuery();

  const validationSchema = Yup.object({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    email: Yup.string().email("Enter valid email").required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9]{11}$/, "Phone number must be 11 digits")
      .required("Phone number is required"),
    dob: Yup.string()
      .nullable()
      .test("not-future", "Date of birth cannot be in the future", function (value) {
        if (!value) return true; // Allow empty (optional)
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate <= today;
      }),
    role_id: canManageRoles
      ? Yup.string().required("Role is required")
      : Yup.string().nullable(),
    branch_id: Yup.string().required("Branch is required"),
    address: Yup.string().required("Address is required"),
    country_id: Yup.string().required("Country is required"),
  });

  const handleFormSubmit = async (
    formikValues,
    { setSubmitting, resetForm }
  ) => {
    if (!canManageRoles) {
      toast.error(
        "Role Management permission is required to create staff and assign a role."
      );
      setSubmitting(false);
      return;
    }
    const updatedFormikValues = {
      ...formikValues,
      branch_id: parseInt(formikValues.branch_id),
    };
    if (errorImg) {
      setSubmitting(false);
      return;
    }
    const reqData = new FormData();

    for (const key in updatedFormikValues) {
      reqData.append([key], updatedFormikValues[key]);
    }
    // reqData.append("branch_id", userData?.profile?.active_branch_id);

    // Only upload image if provided
    if (imgFile) {
      const cloudName =
        process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "afrilocales";
      const uploadPreset =
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "svmprhvf";
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

      const photoData = new FormData();
      photoData.append("file", imgFile);
      photoData.append("upload_preset", uploadPreset);
      const photoUrl = await axios
        .post(uploadUrl, photoData)
        .then((response) => {
          return response?.data?.secure_url;
        })
        .catch(() => {
          return "https://blog.namely.com/hs-fs/hubfs/biweekly-1.png?width=2400&name=biweekly-1.png";
        });
      reqData.append("photo", photoUrl);
    }
    try {
      const payload = await createStaff(reqData).unwrap();
      if (payload.status === false) {
        toast.error(payload?.message || "An error occurred");
        return;
      }
      toast.success("Staff successfully created");
      resetForm();
      setImgFile(null);
      setImgPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error(error?.data?.message || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {({ isSubmitting }) => (
        <>
          {isSubmitting && (
            <FormLoadingOverlay
              message="Creating Staff..."
              subMessage="Please wait while we process your registration"
            />
          )}
          <Form
            style={{
              position: "relative",
              opacity: isSubmitting ? 0.6 : 1,
              pointerEvents: isSubmitting ? "none" : "auto",
            }}
          >
            <section className="px-3 px-lg-5 min-vh-100 pb-5">
          {/* Header Section */}
          <CardComp cardClassName={"border-0 mb-4 shadow-sm"}>
            <div className="px-3 px-lg-4 py-3">
              <h3 className="mb-0 text-dark fw-bold" style={{ fontSize: "24px" }}>
                Staff Registration
              </h3>
              <p className="text-muted mb-0 mt-2" style={{ fontSize: "14px" }}>
                Fill in the details below to register a new staff member
              </p>
              {!canManageRoles ? (
                <div
                  className="mt-3 p-3 rounded-lg"
                  style={{
                    backgroundColor: "rgba(255, 149, 0, 0.1)",
                    border: "1px solid #ff9500",
                  }}
                >
                  <p className="mb-0 text-sm" style={{ color: "#383838" }}>
                    You need <strong>Role Management</strong> permission to create
                    staff and assign a role. Ask a Super Admin to grant it, or ask
                    them to create this account.
                  </p>
                </div>
              ) : null}
            </div>
          </CardComp>

          {/* Form Section */}
          <CardComp cardClassName={"border-0 shadow-sm"}>
            <div className="px-3 px-lg-4 py-4">
              <div className="row g-4">
                <div className="col-12 col-lg-6">
                  <TextInputComp
                    LabelText={"First name"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-bold text-dark"}
                    name="first_name"
                    placeholder="Please enter your first name"
                    type="text"
                  />
                </div>
                <div className="col-12 col-lg-6">
                  <TextInputComp
                    LabelText={"Last name"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-bold text-dark"}
                    name="last_name"
                    placeholder="Please enter your last name"
                    type="text"
                  />
                </div>
                <div className="col-12 col-lg-6">
                  <TextInputComp
                    LabelText={"Email"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-bold text-dark"}
                    name="email"
                    placeholder="Please enter your email address"
                    type="email"
                  />
                </div>
                <div className="col-12 col-lg-6">
                  <TextInputComp
                    LabelText={"Phone number"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-bold text-dark"}
                    type="text"
                    name="phone"
                    placeholder="Please enter your phone number"
                  />
                </div>
                <div className="col-12 col-lg-6">
                  <FormikDatePicker
                    fieldName={"dob"}
                    labelText={"Date of birth"}
                    labelClassName={"fw-bold text-dark"}
                    maxDate={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div className="col-12 col-lg-6">
                  {canManageRoles ? (
                    <FormikSelectComp
                      labelClassName={"fw-bold text-dark"}
                      fieldName={"role_id"}
                      labelText={"Role"}
                      selectText="Select role"
                      options={assignableRoles.map((role) => ({
                        value: role.id,
                        label: role.name,
                      }))}
                    />
                  ) : (
                    <div>
                      <label className="fw-bold text-dark mb-1">Role</label>
                      <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                        Role Management permission required to assign a role.
                      </p>
                    </div>
                  )}
                </div>
                <div className="col-12 col-lg-6">
                  <FormikSelectComp
                    labelClassName={"fw-bold text-dark"}
                    fieldName={"branch_id"}
                    labelText={"Branch"}
                    selectText="Select branch"
                    options={branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))}
                  />
                </div>
                <div className="col-12 col-lg-6">
                  <TextInputComp
                    LabelText={"Address"}
                    placeholder={"Aare avenue, Bodija, Ibadan, Oyo state"}
                    borderColor="#A1A0A0"
                    LabelClassName={"fw-bold text-dark"}
                    type="text"
                    name="address"
                  />
                </div>
                <div className="col-12 col-lg-6">
                  <FormikSelectComp
                    labelClassName={"fw-bold text-dark"}
                    fieldName={"country_id"}
                    labelText={"Country"}
                    selectText="Select country"
                    options={countries.map((country) => ({
                      value: country.id,
                      label: country.name,
                    }))}
                  />
                </div>
                {/* Profile Image Upload */}
                <div className="col-12 col-lg-5">
                  <label
                    className="fw-bold text-dark mb-2"
                    style={{ fontSize: "14px", display: "block" }}
                  >
                    Profile Image
                  </label>
                  <div className="position-relative">
                    {imgPreview ? (
                      <div className="position-relative d-inline-block">
                        <div
                          className="rounded"
                          style={{
                            width: "120px",
                            height: "120px",
                            overflow: "hidden",
                            border: "2px solid #E0E0E0",
                            backgroundColor: "#FAFAFA",
                          }}
                        >
                          <FallbackImage
                            src={imgPreview}
                            alt="Profile preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="btn btn-sm position-absolute top-0 end-0 rounded-circle bg-white border shadow-sm"
                          style={{
                            width: "28px",
                            height: "28px",
                            padding: 0,
                            transform: "translate(50%, -50%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiX size={14} color="#666" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="imgFile"
                        className="d-inline-block"
                        style={{ cursor: "pointer" }}
                      >
                        <div
                          className="d-flex flex-column align-items-center justify-content-center rounded border-2 border-dashed"
                          style={{
                            width: "120px",
                            height: "120px",
                            backgroundColor: "#FAFAFA",
                            borderColor: errorImg ? "#FF4343" : "#E0E0E0",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            if (!errorImg) {
                              e.currentTarget.style.borderColor = "var(--color-primary)";
                              e.currentTarget.style.backgroundColor = "#FFF5F5";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!errorImg) {
                              e.currentTarget.style.borderColor = "#E0E0E0";
                              e.currentTarget.style.backgroundColor = "#FAFAFA";
                            }
                          }}
                        >
                          <FiUpload
                            size={24}
                            color={errorImg ? "#FF4343" : "var(--color-primary)"}
                          />
                          <span
                            className="mt-2 text-center"
                            style={{
                              fontSize: "11px",
                              color: errorImg ? "#FF4343" : "#666",
                              fontWeight: 500,
                            }}
                          >
                            Click to upload
                          </span>
                        </div>
                      </label>
                    )}
                    <input
                      ref={fileInputRef}
                      onChange={updateImgFile}
                      name="file"
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      style={{ display: "none" }}
                      id="imgFile"
                    />
                  </div>
                  {imgFile && !errorImg && (
                    <small className="text-muted mt-2 d-block" style={{ fontSize: "12px" }}>
                      {imgFile?.name}
                    </small>
                  )}
                  {errorImg && (
                    <small className="text-danger mt-2 d-block" style={{ fontSize: "12px" }}>
                      {errorImg}
                    </small>
                  )}
                  {!errorImg && !imgFile && (
                    <small className="text-muted mt-2 d-block" style={{ fontSize: "12px" }}>
                      JPG, PNG or JPEG (Max. 10MB)
                    </small>
                  )}
                </div>
              </div>

              {/* Submit Button Section */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="d-flex justify-content-end pt-3 border-top">
                    <ButtonComp
                      type={"submit"}
                      loading={isSubmitting}
                      disabled={
                        isLoadingBranches || isSubmitting || !canManageRoles
                      }
                      btnText={isSubmitting ? "Creating Staff..." : "Submit"}
                      btnClassName={
                        `bg-primary1 py-2 px-5 text-white border-0 rounded fw-semibold ${
                          isSubmitting ? "opacity-75" : ""
                        }`
                      }
                      style={{ minWidth: "150px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardComp>
        </section>
      </Form>
        </>
      )}
    </Formik>
  );
};
